/**
 * CTA Buttons Component for ExpauseSheet
 *
 * Renders action buttons for sheets with configurable layout and styling.
 * - Row layout: buttons side by side (typical for landscape/dialog)
 * - Column layout: stacked buttons with separators (typical for portrait/bottom sheet)
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useIsLandscape, useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { ExpauseLoadingIndicator } from '@/presentation/components/common/loadingIndicators/ExpauseLoadingIndicator';
import { qaAttr } from '@/presentation/utils/qaAttr';
import type { SheetState, SheetButton } from '../config/ExpauseSheet.types';

interface CTAButtonsProps {
  cta: SheetState['cta'];
  onClose: () => void;
}

/**
 * CTA Buttons Component
 */
export const CTAButtons: React.FC<CTAButtonsProps> = ({ cta, onClose }) => {
  const { theme } = useTheme();
  const responsiveDesign = useResponsiveDesign();
  const isLandscape = useIsLandscape();

  if (!cta) return null;

  // Default orientation based on landscape state
  const orientation = cta.orientation || (isLandscape ? 'row' : 'column');

  // Default variant based on landscape state
  const defaultVariant = isLandscape ? 'primary' : 'label';

  // Normalize buttons to ensure they all have a variant
  const normalizedButtons = cta.buttons.map((button) => ({
    ...button,
    variant: button.variant || defaultVariant,
  }));

  const getButtonStyle = (button: SheetButton) => {
    const variant = button.variant || defaultVariant;
    let backgroundColor = variant === 'danger' ? theme.danger : theme.transparent;
    let textColor = button.textColor || theme.text.primary;

    return { backgroundColor, color: textColor };
  };

  // Resolve the `data-qa-id` for a CTA button: per-button override wins,
  // otherwise fall back to positional ids so the qa-tester can always find a
  // sheet's primary (first) and secondary (rest) actions.
  const getButtonQaId = (button: SheetButton, index: number): string =>
    button.qaId ?? (index === 0 ? 'sheet-cta-primary' : 'sheet-cta-secondary');

  const handleButtonClick = async (button: SheetButton) => {
    await button.onClick();
    // Only auto-close if closeOnClick is not explicitly set to false
    if (button.closeOnClick !== false) {
      onClose();
    }
  };

  if (orientation === 'row') {
    // Row layout: buttons side by side
    return (
      <div
        className="flex border-t"
        style={{
          gap: responsiveDesign.gap.base,
          paddingTop: responsiveDesign.spacing.lg,
          paddingBottom: responsiveDesign.spacing.lg,
          borderTopColor: theme.border,
        }}
      >
        {normalizedButtons.map((button, index) => (
          <button
            key={index}
            {...qaAttr({ id: getButtonQaId(button, index) })}
            onClick={() => handleButtonClick(button)}
            disabled={button.disabled}
            className="flex-1 font-medium transition-opacity hover:opacity-80 disabled:opacity-50"
            style={{
              paddingTop: responsiveDesign.spacing.base,
              paddingBottom: responsiveDesign.spacing.base,
              paddingLeft: responsiveDesign.spacing.lg,
              paddingRight: responsiveDesign.spacing.lg,
              borderRadius: responsiveDesign.borderRadius.lg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: responsiveDesign.gap.sm,
              ...getButtonStyle(button),
            }}
          >
            {/* Left spacer to balance the loading indicator */}
            <div style={{ width: 14 }} />
            {button.text}
            {/* Right side - loading indicator or spacer */}
            <div style={{ width: 14, display: 'flex', justifyContent: 'center' }}>
              {button.loading && <ExpauseLoadingIndicator size={14} />}
            </div>
          </button>
        ))}
      </div>
    );
  }

  // Column layout: stacked buttons with separators
  return (
    <div className="border-t" style={{ borderTopColor: theme.border }}>
      {normalizedButtons.map((button, index) => (
        <React.Fragment key={index}>
          {index > 0 && (
            <div className="h-px" style={{ backgroundColor: theme.border }} />
          )}
          <button
            {...qaAttr({ id: getButtonQaId(button, index) })}
            onClick={() => handleButtonClick(button)}
            disabled={button.disabled}
            className="w-full font-medium transition-opacity hover:opacity-80 disabled:opacity-50"
            style={{
              paddingTop: responsiveDesign.spacing.lg,
              paddingBottom: responsiveDesign.spacing.lg,
              paddingLeft: responsiveDesign.spacing['2xl'],
              paddingRight: responsiveDesign.spacing['2xl'],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: responsiveDesign.gap.sm,
              ...getButtonStyle(button),
            }}
          >
            {/* Left spacer to balance the loading indicator */}
            <div style={{ width: 14 }} />
            {button.text}
            {/* Right side - loading indicator or spacer */}
            <div style={{ width: 14, display: 'flex', justifyContent: 'center' }}>
              {button.loading && <ExpauseLoadingIndicator size={14} />}
            </div>
          </button>
        </React.Fragment>
      ))}
    </div>
  );
};
